import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';
import { DateEx } from '../../../functions/date-ex';
import { getClaimKey } from '../../../functions/get-claim-key';
import { ClaimRow } from '../../../models/api/view/claim-view/claim-row';
import { FormatClaimPipe } from '../../../pipes/format-claim.pipe';
import { Format, FormatPipe } from '../../../pipes/format.pipe';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ToggleSwitchComponent } from '../../core/controls/toggle-switch.component';
import { SsnLabelComponent } from '../../core/labels/ssn-label.component';
import { TagComponent, TagType } from '../../core/labels/tag.component';
import { DetailItemComponent } from '../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../core/panels/detail-panel/detail-panel.component';
import { DrawerComponent } from '../../core/panels/drawer.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';
import { ClaimHeaderDialogComponent } from './claim-header-dialog.component';

@Component({
  selector: 'app-claim-header',
  imports: [
    CommonModule,
    DrawerComponent,
    ButtonComponent,
    ToggleSwitchComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TagComponent,
    ClaimHeaderDialogComponent,
    SsnLabelComponent,
    LoadingPanelComponent,
    FormatPipe,
  ],
  template: `
    <div class="tw-min-h-[50px] tw-pb-4">
      <app-loading-panel
        [state]="state"
        [loadingHeight]="claimRow?.lineOfBusiness === 'DS' ? 82 : 58"
        loadingStyle="Box"
        [error]="error"
        ><ng-template #loaded>
          <div
            class="tw-flex tw-flex-wrap md:tw-flex-nowrap tw-w-full tw-items-center tw-mt-2 tw-py-1 tw-pl-2 tw-gap-3 tw-bg-[var(--sed-open-grey-color)]
      tw-rounded-md tw-min-w-0 tw-min-h-[50px]"
          >
            <app-claim-header-dialog
              #headerDialog
              label="Configure header"
              [headerFields]="fields"
              (headerFieldsChange)="onHeaderFieldsChange($event)"
            />
            <div
              class="tw-hidden sm:tw-flex tw-flex-shrink-0 tw-pb-0.5 tw-pr-2"
            >
              <button
                type="button"
                class="tw-border-none tw-bg-transparent tw-cursor-pointer
         tw-inline-flex tw-items-center tw-justify-center
         tw-min-w-[24px] tw-min-h-[24px] tw-p-0"
                aria-label="Configure header"
                (click)="headerDialog.visible = true"
              >
                <i class="fa-light fa-pen" aria-hidden="true"></i>
              </button>
            </div>
            <app-detail-panel
              [dataSource]="claimRow"
              class="tw-flex-1 tw-min-w-0 max-md:tw-flex-col"
              [row]="true"
              [showEmpty]="false"
              showEmptySource="item"
            >
              @for (field of fields; track field) {
                @if (!field.visible || field.visible.value) {
                  @if (
                    field.label === 'Status' ||
                    field.label === 'Active Status - Absence Status'
                  ) {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      [format]="field.format"
                      [showEmpty]="false"
                      classes="tw-basis-1/5 max-md:tw-basis-full max-md:tw-justify-between max-md:tw-pr-2"
                    >
                      <ng-template #value>
                        <app-tag [type]="statusType"
                          >{{ claimRow.status }}
                          @if (
                            showSubStatus &&
                            (claimRow.subStatus || claimRow.subReason)
                          ) {
                            -
                            {{
                              claimRow.subStatus
                                ? claimRow.subStatus
                                : claimRow.subReason
                            }}
                          }
                        </app-tag>
                      </ng-template>
                    </app-detail-item>
                  } @else if (field.label === 'SSN') {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      classes="tw-basis-1/5 max-md:tw-basis-full max-md:tw-justify-between max-md:tw-pr-2"
                      labelCase="upper"
                    >
                      <ng-template #value>
                        <app-ssn-label [value]="claimRow.sSN" />
                      </ng-template>
                    </app-detail-item>
                  } @else if (
                    field.label === 'Phone #' ||
                    field.label === 'Examiner' + ' #' ||
                    field.label === 'Specialist #'
                  ) {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      classes="tw-basis-1/5 max-md:tw-basis-full max-md:tw-justify-between max-md:tw-pr-2"
                    >
                      <ng-template #value>
                        {{
                          field.label === 'Phone #'
                            ? (claimRow.claimantPhone | format: 'p')
                            : (claimRow.examinerNumber | format: 'p')
                        }}
                      </ng-template>
                    </app-detail-item>
                  } @else if (field.label === 'Substatus') {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      [classes]="
                        (field.value?.length > 20
                          ? 'tw-basis-2/5'
                          : 'tw-basis-1/5') +
                        ' max-sm:tw-basis-full max-sm:tw-justify-between max-sm:tw-pr-2'
                      "
                    >
                      <ng-template #value>
                        <app-tag [type]="statusType">{{
                          claimRow.subStatus
                        }}</app-tag>
                      </ng-template>
                    </app-detail-item>
                  } @else if (field.label === 'Client Name') {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      [classes]="
                        (claimRow.client?.length > 20
                          ? 'tw-basis-2/5'
                          : 'tw-basis-1/5') +
                        ' max-sm:tw-basis-full max-sm:tw-justify-between max-sm:tw-pr-2'
                      "
                    >
                      <ng-template #value>
                        {{ claimRow.client }}
                      </ng-template>
                    </app-detail-item>
                  } @else if (
                    field.label === 'Examiner office' ||
                    field.label === 'Proc. office'
                  ) {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      [classes]="
                        ((field.name === 'examinerOffice'
                          ? claimRow.examinerOffice
                          : claimRow.procOffice
                        )?.length > 20
                          ? 'tw-basis-2/5'
                          : 'tw-basis-1/5') +
                        ' max-sm:tw-basis-full max-sm:tw-justify-between max-sm:tw-pr-2'
                      "
                    ></app-detail-item>
                  } @else if (
                    field.label === 'Opened' || field.label === 'Closed'
                  ) {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      [format]="field.format"
                      classes="tw-basis-1/5 max-md:tw-basis-full max-md:tw-justify-between max-md:tw-pr-2"
                    ></app-detail-item>
                  } @else if (
                    field.label === 'Examiner' ||
                    field.label === 'Leave Specialist'
                  ) {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      classes="tw-basis-1/5 max-md:tw-basis-full max-md:tw-justify-between max-md:tw-pr-2"
                    >
                      <ng-template #value>
                        @if (
                          claimRow.examinerEmail && showExaminerEmailLink()
                        ) {
                          <a
                            [href]="getExaminerMailtoLink()"
                            (click)="openExaminerEmail($event)"
                            class="hyperlink tw-underline hover:tw-text-[var(--sed-engaged-blue-color)]"
                            >{{ claimRow.examinerName }}</a
                          >
                        } @else {
                          {{ claimRow.examinerName }}
                        }
                      </ng-template>
                    </app-detail-item>
                  } @else if (field.isMiscField) {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      classes="tw-basis-1/5 max-md:tw-basis-full max-md:tw-justify-between max-md:tw-pr-2"
                    >
                      <ng-template #value>
                        {{ field.value }}
                      </ng-template>
                    </app-detail-item>
                  } @else {
                    <app-detail-item
                      [label]="field.label"
                      [name]="field.name"
                      [format]="field.format"
                      classes="tw-basis-1/5 max-md:tw-basis-full max-md:tw-justify-between max-md:tw-pr-2"
                    ></app-detail-item>
                  }
                }
              }
            </app-detail-panel>
          </div>

          <app-drawer [(visible)]="showConfig" styleClass="tw-min-w-[400px]">
            <div class="tw-flex tw-flex-col tw-py-8 tw-w-full">
              <div class="tw-flex tw-flex-col">
                @for (field of fields; track field) {
                  @if (field.visible && field.enabled) {
                    <app-toggle-switch
                      [control]="field.visible"
                      [rightLabel]="field.label"
                      classes="tw-font-bold"
                      [helpText]="field.value"
                    />
                  }
                }
              </div>
            </div>
            <app-button label="Close" (click)="showConfig = false" />
          </app-drawer>
        </ng-template>
      </app-loading-panel>
    </div>
  `,
  styles: ``,
})
export class ClaimHeaderComponent extends BaseComponent implements OnInit {
  private formatClaimPipe = inject(FormatClaimPipe);

  @Output() claimRowUpdated = new EventEmitter<ClaimRow>();

  protected state: LoadingState = 'Loading';
  error: any = null;
  claimRow: ClaimRow;
  statusType: TagType;

  showConfig = false;

  fields: HeaderField[] = [];

  fg = new FormGroup({
    items: new FormArray([]),
  });

  showSubStatus = true;
  private lastLoadedClaimId: string = null;

  constructor() {
    super();

    // Watch for claim changes from resolver (handles browser back/forward)
    effect(() => {
      const claim = this.view.currentClaim();
      if (claim) {
        const claimId = `${claim.recordSource}-${claim.recordId}`;
        // Only reload if claim actually changed
        if (this.lastLoadedClaimId && this.lastLoadedClaimId !== claimId) {
          this.loadClaimHeader(claim.recordSource, claim.recordId);
        }
      }
    });
  }

  add(name: string, label: string = null, format: Format = null): void {
    // Check if field already exists
    if (this.fields.some((f) => f.name === name)) {
      return;
    }

    const fc = new FormControl<boolean>(false);
    this.fg.controls.items.push(fc);
    this.fields.push(
      new HeaderField(label, name, (this.claimRow as any)[name], format, fc),
    );
  }

  enableOptionalField(name: string): void {
    const field = this.fields.find((f) => f.name === name);
    if (field) {
      field.enabled = true;
    }
  }

  @HandleLoadingState()
  async ngOnInit() {
    await this.loadClaimHeader();
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  private async loadClaimHeader(recordSource?: string, recordId?: string) {
    this.fields = [];

    const claimKey =
      recordSource && recordId ? { recordSource, recordId } : getClaimKey();

    if (claimKey) {
      this.lastLoadedClaimId = `${claimKey.recordSource}-${claimKey.recordId}`;

      this.claimRow = await this.api.claimHeader.get(claimKey);
      if (this.claimRow.lineOfBusiness === 'DS') {
        await this.api.claimSummary
          .getClaimSummaryInfo(
            this.claimRow.recordSource,
            this.claimRow.recordId,
          )
          .then((data) => {
            if (data) {
              this.claimRow.subStatus = data.claimSubStatus;
              this.claimRow.subReason = data.claimSubReason;
            }
          });
      }
      await this.loadAdditionalData();
      this.registerFields();
      this.setStatusType();

      // Load selected fields configuration to process Misc Fields (bypass cache for fresh config)
      const selectedConfigs = await this.api.claimHeaderDialog.getSelected();
      const contractNumber = this.claimRow.contractNumber
        ? Number.parseInt(this.claimRow.contractNumber)
        : null;
      const selectedByContract = selectedConfigs.find(
        (x) =>
          x.contractNumber === contractNumber &&
          x.lineOfBusiness === this.claimRow.lineOfBusiness,
      );
      const selectedByLob = selectedConfigs.find(
        (x) =>
          x.lineOfBusiness === this.claimRow.lineOfBusiness &&
          !x.contractNumber,
      );
      const selectedConfig = selectedByContract || selectedByLob;

      if (selectedConfig) {
        const selectedFieldNames = selectedConfig.items.map((f) => f.name);
        await this.onHeaderFieldsChange(selectedFieldNames);
      }

      // Emit the updated claim row to parent component
      this.claimRowUpdated.emit(this.claimRow);
    }
  }

  toggleShowConfig() {
    this.showConfig = true;
  }

  registerFields(): void {
    this.add('status', 'Status');
    this.add('lossDate', 'Date of loss ', 'd');
    this.add('dateCreate', 'Created', 'd');
    this.add('dateOpen', 'Opened', 'd');
    this.add('dateClosed', 'Closed', 'd');
    this.add('dateClaimLastReopened', 'Reopened', 'd');
    this.add('examinerName', 'Examiner');
    this.add('examinerOffice', 'Examiner office');
    this.add('procOffice', 'Proc. office');
    this.add('client', 'Client Name');
    this.add('contractNumber', 'Client #');
    this.add('caseType', 'Case Type');
    this.add('dateBegin', 'Date Begin', 'd');
    this.add('dateEnd', 'Date End', 'd');
    this.add('specialistNumber', 'Examiner phone number');
    this.add('workState', 'Work state/Province');
    this.add('relationship', 'Relationship');
    this.add('caringFor', 'Caring for');
    this.add('gender', 'Gender');
    this.add('hoursWorked', 'Hours worked in last 12 months');
    this.add('phoneNumber', 'Phone #');
    this.add('ssn', 'SSN');
    this.add('subStatusCode', 'Sub status code');
    this.add('subType', 'Sub type');
    this.add('employeeId', 'Employee ID');
    this.add('policyNumber', 'Policy number');
    this.add('autoDriverName', 'Auto driver name');
    this.add('firstAbsenceDate', 'First absence', 'd');
    this.add('dateLastWorked', 'Last worked', 'd');
    this.add('examinerNumber', 'Examiner phone number', 'p');
    this.add('spouseAtSameClient', 'Spouse at same client', 'b');

    // TAMS Accommodation fields
    this.add('requestNumber', 'Request #');
    this.add('accommodationType', 'Accommodation Type');
    this.add('inMaintenance', 'In Maintenance');
    this.add('onLegalHold', 'On Legal Hold');
    this.add('entityContactName', 'Entity Contact');
    this.add('entityContactEmail', 'Entity Email');
    this.add('userCorrName', 'User Contact');
    this.add('userEmail', 'User Email');
    this.add('specialistCorrName', 'Specialist Contact');
    this.add('specEmail', 'Specialist Email');
    this.add('specCorrName', 'Specialist Contact Name');
    this.add('subStatus', 'Substatus');
    this.add('intakeDate', 'Intake date', 'd');

    // JURIS Accommodation fields
    this.add('empUnum', 'Employee Number');
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onHeaderFieldsChange(selectedFields: string[]): Promise<void> {
    if (this.fields.length === 0) {
      this.registerFields();
    }

    const hasMcfPermission = this.session.authorize('Miscellaneous');
    const filteredSelectedFields = hasMcfPermission
      ? selectedFields
      : selectedFields.filter((name) => !this.isMiscField(name));

    const selectedMiscFields = filteredSelectedFields.filter((f) =>
      this.isMiscField(f),
    );
    const hasMissingMiscFieldValues = selectedMiscFields.some((fieldName) => {
      const fieldNumber = this.getMiscFieldNumber(fieldName);
      return (
        fieldNumber !== null &&
        (!this.claimRow.miscFieldValues ||
          !(fieldNumber in this.claimRow.miscFieldValues))
      );
    });

    if (hasMissingMiscFieldValues && selectedMiscFields.length > 0) {
      const claimKey = getClaimKey();
      if (claimKey) {
        const freshClaimRow = await this.api.claimHeader.get(claimKey, false);
        this.claimRow.miscFieldValues = freshClaimRow.miscFieldValues;
      }
    }

    const selectedConfigs = await this.api.claimHeaderDialog.getSelected();
    const contractNumber = this.claimRow.contractNumber
      ? Number.parseInt(this.claimRow.contractNumber)
      : null;
    const selectedByContract = selectedConfigs.find(
      (x) =>
        x.contractNumber === contractNumber &&
        x.lineOfBusiness === this.claimRow.lineOfBusiness,
    );
    const selectedByLob = selectedConfigs.find(
      (x) =>
        x.lineOfBusiness === this.claimRow.lineOfBusiness && !x.contractNumber,
    );
    const selectedConfig = selectedByContract || selectedByLob;

    // Create a map of Misc Field labels
    const miscFieldLabels = new Map<string, string>();
    if (selectedConfig) {
      selectedConfig.items.forEach((field) => {
        if (this.isMiscField(field.name)) {
          miscFieldLabels.set(field.name, field.label);
        }
      });
    }

    // Map header field names to restrictedFields keys (WEB_RESTRICT suppression)
    const restrictedFields =
      this.session.user?.fieldSecurity?.restrictedFields ?? [];
    const headerToRestricted: Record<string, string> = {
      dateCreate: 'DateCreate',
      dateOpen: 'DateOpened',
      dateClosed: 'DateClosed',
      dateClaimLastReopened: 'DateClaimLastReopened',
      subType: 'SubType',
      dateRptByClient: 'DateRptByClient',
      dateReptToTPA: 'DateReptToTPA',
    };
    this.fields.forEach((field) => {
      const restrictedName = headerToRestricted[field.name];
      const isRestricted =
        restrictedName != null && restrictedFields.includes(restrictedName);
      field.visible.setValue(
        !isRestricted && filteredSelectedFields.includes(field.name),
      );
    });

    const reorderedFields: HeaderField[] = [];
    const addedFieldNames = new Set<string>();

    // First, add selected fields in order
    filteredSelectedFields.forEach((fieldName) => {
      if (!addedFieldNames.has(fieldName)) {
        let field = this.fields.find((f) => f.name === fieldName);

        // If field not found and it's a Misc Field, create it
        if (!field && this.isMiscField(fieldName)) {
          const fieldNumber = this.getMiscFieldNumber(fieldName);
          if (fieldNumber !== null) {
            const label = miscFieldLabels.get(fieldName) || fieldName;
            const value = this.claimRow.miscFieldValues?.[fieldNumber] || '';
            const fc = new FormControl<boolean>(true);
            field = new HeaderField(
              label,
              fieldName,
              value,
              null,
              fc,
              true,
              true,
              fieldNumber,
            );
            this.fg.controls.items.push(fc);
            this.fields.push(field);
          }
        }

        if (field) {
          if (field.isMiscField && field.miscFieldNumber !== undefined) {
            field.value =
              this.claimRow.miscFieldValues?.[field.miscFieldNumber] || '';
          } else {
            // Refresh non-Misc field values from claimRow
            field.value = (this.claimRow as any)[field.name] ?? field.value;
          }

          reorderedFields.push(field);
          addedFieldNames.add(fieldName);
        }
      }
    });

    // Then, add remaining fields
    this.fields.forEach((field) => {
      if (!addedFieldNames.has(field.name)) {
        reorderedFields.push(field);
        addedFieldNames.add(field.name);
      }
    });

    this.fields = reorderedFields;
  }

  private isMiscField(fieldName: string): boolean {
    // Misc Fields are stored with field number as name (e.g., "1", "2", "123")
    return /^\d+$/.test(fieldName);
  }

  private getMiscFieldNumber(fieldName: string): number | null {
    const num = parseInt(fieldName, 10);
    return isNaN(num) ? null : num;
  }

  getClaimStatusValue(Status: string, SubStatus?: string): string {
    if (Status === 'Incident') {
      return Status;
    }

    let result = Status || '';
    if (SubStatus) {
      result += ' - ' + SubStatus;
    }
    return result;
  }
  @HandleLoadingState()
  async loadAdditionalData() {
    // Load additional data for WC, GL, AU, PR claims
    if (['WC', 'GL', 'AU', 'PR'].includes(this.claimRow.lineOfBusiness)) {
      const claimSummaryInfo = await this.api.claimSummary.getClaimSummaryInfo(
        this.claimRow.recordSource,
        this.claimRow.recordId,
      );
      if (claimSummaryInfo.claimSubStatus) {
        this.claimRow.subStatus = claimSummaryInfo.claimSubStatus;
      }
      if (claimSummaryInfo.claimSubReason) {
        this.claimRow.subReason = claimSummaryInfo.claimSubReason;
      }
      // Ensure dateOpen is populated
      if (claimSummaryInfo.dateOpened) {
        this.claimRow.dateOpen = claimSummaryInfo.dateOpened;
      }
      // Ensure dateClosed is populated
      if (claimSummaryInfo.dateClosed) {
        this.claimRow.dateClosed = claimSummaryInfo.dateClosed;
      }
      // Ensure dateCreate is populated
      if (claimSummaryInfo.dateCreated) {
        this.claimRow.dateCreate = claimSummaryInfo.dateCreated;
      }

      // Load examiner name, policy number, and reopened date from additional data
      const additionalData = await this.api.claimHeader.getAdditionalData(
        this.claimRow.recordSource,
        this.claimRow.recordId,
        this.claimRow.lineOfBusiness,
      );
      if (additionalData.examinerName) {
        this.claimRow.examinerName = additionalData.examinerName;
      }
      if (additionalData.examinerEmail) {
        this.claimRow.examinerEmail = additionalData.examinerEmail;
      }
      if (additionalData.policyNumber) {
        this.claimRow.policyNumber = additionalData.policyNumber;
      }
      if (additionalData.reopened) {
        this.claimRow.dateClaimLastReopened = DateEx.formatFullDate(
          new Date(additionalData.reopened),
        );
      }
      if (additionalData.subType) {
        this.claimRow.subType = additionalData.subType;
      }
      // AU-specific field
      if (
        this.claimRow.lineOfBusiness === 'AU' &&
        additionalData.autoDriverName
      ) {
        this.claimRow.autoDriverName = additionalData.autoDriverName;
      }
      // Explicit closed date from additional data as fallback
      if (additionalData.closed && !this.claimRow.dateClosed) {
        this.claimRow.dateClosed = DateEx.formatFullDate(
          new Date(additionalData.closed),
        );
      }
      // Override claimant name with source data (preserves hyphens/apostrophes)
      if (additionalData.claimantFirstName) {
        this.claimRow.claimantFirstName = additionalData.claimantFirstName;
      }
      if (additionalData.claimantLastName) {
        this.claimRow.claimantLastName = additionalData.claimantLastName;
      }
      this.claimRow.examinerOffice = additionalData.examinerOffice ?? '';
      this.claimRow.procOffice = additionalData.procOffice ?? '';
    }

    if (
      this.claimRow.lineOfBusiness === 'DS' &&
      this.claimRow.coverageCode !== 'AC'
    ) {
      const claimSummaryInfo = await this.api.claimSummary.getClaimSummaryInfo(
        this.claimRow.recordSource,
        this.claimRow.recordId,
      );
      this.claimRow.dateOpen = claimSummaryInfo.dateOpened;
      this.claimRow.dateClosed = claimSummaryInfo.dateClosed;
      this.claimRow.lossDate = claimSummaryInfo.lossDate;
      this.claimRow.subReason = claimSummaryInfo.claimSubReason;
      this.claimRow.subStatus = claimSummaryInfo.claimSubStatus;

      const dsAdditionalData = await this.api.claimHeader.getAdditionalData(
        this.claimRow.recordSource,
        this.claimRow.recordId,
        this.claimRow.lineOfBusiness,
      );
      this.claimRow.firstAbsenceDate = dsAdditionalData.dateFirstAbsent
        ? DateEx.formatFullDate(dsAdditionalData.dateFirstAbsent as any)
        : '';
      this.claimRow.dateLastWorked = dsAdditionalData.dateLastWorked
        ? DateEx.formatFullDate(dsAdditionalData.dateLastWorked as any)
        : '';
      this.claimRow.dateClaimLastReopened = dsAdditionalData.reopened
        ? DateEx.formatFullDate(new Date(dsAdditionalData.reopened))
        : '';
      this.claimRow.examinerName = dsAdditionalData.examinerName;
      this.claimRow.examinerEmail = dsAdditionalData.examinerEmail;
      if (dsAdditionalData.subType) {
        this.claimRow.subType = dsAdditionalData.subType;
      }
      this.claimRow.empUnum = dsAdditionalData.empUnum;
      // Explicit closed date from additional data as fallback
      if (dsAdditionalData.closed && !this.claimRow.dateClosed) {
        this.claimRow.dateClosed = DateEx.formatFullDate(
          new Date(dsAdditionalData.closed),
        );
      }
      // Override claimant name with source data (preserves hyphens/apostrophes)
      if (dsAdditionalData.claimantFirstName) {
        this.claimRow.claimantFirstName = dsAdditionalData.claimantFirstName;
      }
      if (dsAdditionalData.claimantLastName) {
        this.claimRow.claimantLastName = dsAdditionalData.claimantLastName;
      }
      this.claimRow.examinerOffice = dsAdditionalData.examinerOffice ?? '';
      this.claimRow.procOffice = dsAdditionalData.procOffice ?? '';
    }
    if (
      this.claimRow.lineOfBusiness === 'LA' ||
      this.claimRow.lineOfBusiness === 'LV'
    ) {
      // Leave claims: additional-data route uses LA loader (search leaveLOB pattern); UI LOB stays LA/LV.
      const leaveClaimSummaryInfo =
        await this.api.claimHeader.getAdditionalData(
          this.claimRow.recordSource,
          this.claimRow.recordId,
          'LA',
        );

      this.claimRow.status = leaveClaimSummaryInfo.activeStatus;
      this.claimRow.subStatus = leaveClaimSummaryInfo.absenceStatus;
      this.claimRow.caseType = leaveClaimSummaryInfo.caseType;
      this.claimRow.relationship = leaveClaimSummaryInfo.relationship;
      this.claimRow.dateBegin = DateEx.formatFullDate(
        new Date(leaveClaimSummaryInfo.beginDate),
      );
      this.claimRow.dateEnd = DateEx.formatFullDate(
        new Date(leaveClaimSummaryInfo.endDate),
      );
      this.claimRow.subType = leaveClaimSummaryInfo.absenceType;
      this.claimRow.hoursWorked = leaveClaimSummaryInfo.hoursWorked;
      this.claimRow.phoneNumber = leaveClaimSummaryInfo.phoneNumber;
      this.claimRow.caringFor = leaveClaimSummaryInfo.caringFor;
      this.claimRow.specialistNumber = leaveClaimSummaryInfo.examinerPhone;
      // Always apply (including empty) so blank Emp ID clears any stale claimRow value
      if (leaveClaimSummaryInfo.employeeId !== undefined) {
        this.claimRow.employeeId = leaveClaimSummaryInfo.employeeId ?? '';
      }
      this.claimRow.sSN = leaveClaimSummaryInfo.ssn ?? null;
      this.claimRow.claimantPhone = leaveClaimSummaryInfo.phoneNumber;
      this.claimRow.spouseAtSameClient =
        leaveClaimSummaryInfo.spouseAtSameClient;
      this.claimRow.examinerNumber = leaveClaimSummaryInfo.examinerPhone;
      this.claimRow.gender = leaveClaimSummaryInfo.gender;
      this.claimRow.examinerName = leaveClaimSummaryInfo.examinerName;
      this.claimRow.examinerEmail = leaveClaimSummaryInfo.examinerEmail;
      this.claimRow.workState = leaveClaimSummaryInfo.workState;
      // Override claimant name with source data (preserves hyphens/apostrophes)
      if (leaveClaimSummaryInfo.claimantFirstName) {
        this.claimRow.claimantFirstName =
          leaveClaimSummaryInfo.claimantFirstName;
      }
      if (leaveClaimSummaryInfo.claimantLastName) {
        this.claimRow.claimantLastName = leaveClaimSummaryInfo.claimantLastName;
      }
    }

    // Add TAMS Accommodation logic
    if (
      this.claimRow.lineOfBusiness === 'AD' &&
      this.claimRow.recordSource === 'R'
    ) {
      const tamsAdditionalData = await this.api.claimHeader.getAdditionalData(
        this.claimRow.recordSource,
        this.claimRow.recordId,
        this.claimRow.lineOfBusiness,
      );

      this.claimRow.requestNumber = tamsAdditionalData.requestNumber;
      this.claimRow.accommodationType = tamsAdditionalData.accommodationType;
      this.claimRow.type = tamsAdditionalData.accommodationType; // Map to existing 'type' property
      this.claimRow.intakeDate = tamsAdditionalData.intakeDate
        ? DateEx.formatFullDate(new Date(tamsAdditionalData.intakeDate))
        : '';
      this.claimRow.inMaintenance = tamsAdditionalData.inMaintenance;
      this.claimRow.onLegalHold = tamsAdditionalData.onLegalHold;
      this.claimRow.entityContactName = tamsAdditionalData.entityContactName;
      this.claimRow.entityContactEmail = tamsAdditionalData.entityContactEmail;
      this.claimRow.userCorrName = tamsAdditionalData.userCorrName;
      this.claimRow.userEmail = tamsAdditionalData.userEmail;
      this.claimRow.specialistCorrName = tamsAdditionalData.specialistCorrName;
      this.claimRow.specEmail = tamsAdditionalData.specEmail;
      this.claimRow.specCorrName = tamsAdditionalData.specCorrName;
      this.claimRow.examinerName = tamsAdditionalData.specialistCorrName; // Use specialist as examiner for TAMS
      // Classic ADA TAMS header mailto: EntityContactEmail, else UserEmail, else SpecEmail
      this.claimRow.examinerEmail =
        tamsAdditionalData.entityContactEmail ||
        tamsAdditionalData.userEmail ||
        tamsAdditionalData.specEmail;
      this.claimRow.status = tamsAdditionalData.absenceStatus;
      this.claimRow.subStatus = tamsAdditionalData.absenceSubstatus;
      this.claimRow.employeeId = tamsAdditionalData.employeeId;
      this.claimRow.client = tamsAdditionalData.clientName;
      this.claimRow.contractNumber =
        tamsAdditionalData.clientNumber?.toString();
    }

    // Add JURIS Accommodation logic
    if (
      this.claimRow.lineOfBusiness === 'DS' &&
      this.claimRow.coverageCode === 'AC'
    ) {
      const jurisAdditionalData = await this.api.claimHeader.getAdditionalData(
        this.claimRow.recordSource,
        this.claimRow.recordId,
        this.claimRow.lineOfBusiness,
      );
      this.claimRow.empUnum = jurisAdditionalData.empUnum;
      this.claimRow.sSN = jurisAdditionalData.ssn;
      this.claimRow.examinerName = jurisAdditionalData.examinerName;
      this.claimRow.examinerEmail = jurisAdditionalData.examinerEmail;
      this.claimRow.client = jurisAdditionalData.clientName;
      this.claimRow.contractNumber =
        jurisAdditionalData.clientNumber?.toString();

      this.claimRow.dateOpen = jurisAdditionalData.opened
        ? DateEx.formatFullDate(new Date(jurisAdditionalData.opened))
        : '';
      this.claimRow.dateClosed = jurisAdditionalData.closed
        ? DateEx.formatFullDate(new Date(jurisAdditionalData.closed))
        : '';
      this.claimRow.dateClaimLastReopened = jurisAdditionalData.reopened
        ? DateEx.formatFullDate(new Date(jurisAdditionalData.reopened))
        : '';
      this.claimRow.firstAbsenceDate = jurisAdditionalData.dateFirstAbsent
        ? DateEx.formatFullDate(new Date(jurisAdditionalData.dateFirstAbsent))
        : '';
      this.claimRow.dateLastWorked = jurisAdditionalData.dateLastWorked
        ? DateEx.formatFullDate(new Date(jurisAdditionalData.dateLastWorked))
        : '';
      // Override claimant name with source data (preserves hyphens/apostrophes)
      if (jurisAdditionalData.claimantFirstName) {
        this.claimRow.claimantFirstName = jurisAdditionalData.claimantFirstName;
      }
      if (jurisAdditionalData.claimantLastName) {
        this.claimRow.claimantLastName = jurisAdditionalData.claimantLastName;
      }
      this.claimRow.examinerOffice = jurisAdditionalData.examinerOffice ?? '';
      this.claimRow.procOffice = jurisAdditionalData.procOffice ?? '';
    }
  }
  private setStatusType(): void {
    // Handle TAMS Accommodations (AD)
    if (this.claimRow.lineOfBusiness === 'AD') {
      // Don't concatenate substatus for Accommodation Claims
      this.showSubStatus = false;

      if (this.claimRow.status === 'Open') {
        // Check substatus for special yellow cases
        if (
          this.claimRow.subStatus === 'Under Review' ||
          this.claimRow.subStatus === 'Pending Business Response'
        ) {
          this.statusType = 'Warn'; // Yellow
        } else if (this.claimRow.subStatus === 'Currently accommodated') {
          this.statusType = 'Success'; // Green
        } else {
          this.statusType = 'Warn'; // Default to yellow for other Open statuses
        }
      } else if (this.claimRow.status === 'Closed') {
        this.statusType = 'Error'; // Red
      } else {
        this.statusType = 'Grey'; // Gray for any other status
      }
    }
    // Handle all other LOBs (DS, LA, PR, GL, WC, etc.)
    else {
      if (this.claimRow.status === 'Open') {
        this.statusType = 'Success';
      } else if (this.claimRow.status === 'Closed') {
        this.statusType = 'Error';
      } else if (this.claimRow.status === 'Incident') {
        this.statusType = 'Warn';
        this.showSubStatus = this.claimRow.lineOfBusiness !== 'DS';
      } else {
        this.statusType = 'Grey';
      }
    }
  }

  /**
   * Returns true when the examiner name should render as a mailto hyperlink.
   * Matches Classic: Casualty (WC/GL/AU/PR), DS, AD TAMS, and LA/LV TAMS (LeaveLink excluded).
   */
  showExaminerEmailLink(): boolean {
    if (!this.claimRow?.examinerEmail) {
      return false;
    }

    const lob = this.claimRow.lineOfBusiness;
    const src = this.claimRow.recordSource;

    const isCasualtyOrDs = ['WC', 'GL', 'AU', 'PR', 'DS'].includes(lob);
    const isAdTams = lob === 'AD' && src === 'R';
    const isLeaveTams = (lob === 'LA' || lob === 'LV') && src === 'T';

    return isCasualtyOrDs || isAdTams || isLeaveTams;
  }

  openExaminerEmail(event: MouseEvent): void {
    const link = this.getExaminerMailtoLink();
    if (!link) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (
      this.showExaminerEmailLink() &&
      link.includes(this.claimRow.examinerEmail)
    ) {
      window.location.assign(link);
    }
  }

  /**
   * Generates a mailto link for the examiner with a pre-populated subject line.
   * Similar to legacy ViaOneView functionality.
   */
  getExaminerMailtoLink(): string {
    if (!this.claimRow.examinerEmail) {
      return '';
    }

    let subject;

    if (
      this.claimRow.lineOfBusiness === 'AD' &&
      this.claimRow.recordSource === 'R'
    ) {
      subject = `Accommodation Request Number: ${this.claimRow.requestNumber || ''}     Name: ${this.claimRow.claimantFirstName || ''} ${this.claimRow.claimantLastName || ''}`;
    } else if (
      this.claimRow.lineOfBusiness === 'DS' ||
      this.claimRow.lineOfBusiness === 'LA' ||
      this.claimRow.lineOfBusiness === 'LV'
    ) {
      const rawClaimNumber =
        this.claimRow.lineOfBusiness === 'DS'
          ? this.claimRow.fileNumber || ''
          : this.claimRow.claimNumber || this.claimRow.fileNumber || '';
      const claimNumber = this.formatClaimPipe.transform(
        rawClaimNumber,
        this.claimRow.fileNumFormat,
      );
      const claimantName =
        `${this.claimRow.claimantFirstName || ''} ${this.claimRow.claimantLastName || ''}`.trim();
      const lossDateFormatted = this.claimRow.lossDate
        ? new Date(this.claimRow.lossDate as any).toLocaleDateString('en-US', {
            month: 'numeric',
            day: '2-digit',
            year: 'numeric',
          })
        : '';
      const isLeaveClaim = ['LA', 'LV'].includes(this.claimRow.lineOfBusiness);
      const claimNumberLabel = isLeaveClaim ? 'Absence Number' : 'Claim Number';
      const additionalFieldLabel = isLeaveClaim ? 'Absence Type' : 'Loss Date';
      const claimantNameLabel = isLeaveClaim ? 'Name' : 'Claimant';
      const additionalFieldValue = isLeaveClaim
        ? this.claimRow.subType || ''
        : lossDateFormatted;
      subject = `${claimNumberLabel}: ${claimNumber}     ${claimantNameLabel}: ${claimantName}     ${additionalFieldLabel}: ${additionalFieldValue}`;
    } else {
      // For WC/GL/AU/PR: "Claim Number: X     Claimant: Y     Loss Date: Z"
      const claimNumber = this.formatClaimPipe.transform(
        this.claimRow.claimNumber || this.claimRow.fileNumber || '',
        this.claimRow.fileNumFormat,
      );
      const toTitleCase = (value: string) =>
        value
          ? value
              .toLowerCase()
              .split(' ')
              .map((word) =>
                word
                  .split('-')
                  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                  .join('-'),
              )
              .join(' ')
          : '';
      const claimantName =
        `${toTitleCase(this.claimRow.claimantFirstName || '')} ${toTitleCase(this.claimRow.claimantLastName || '')}`.trim();
      const lossDateFormatted = this.claimRow.lossDate
        ? new Date(this.claimRow.lossDate as any).toLocaleDateString('en-US', {
            month: 'numeric',
            day: '2-digit',
            year: 'numeric',
          })
        : '';
      subject = `Claim Number: ${claimNumber}     Name: ${claimantName}     Loss Date: ${lossDateFormatted}`;
    }

    return `mailto:${this.claimRow.examinerEmail}?subject=${encodeURIComponent(subject)}`;
  }
}

export class HeaderField {
  constructor(
    public label: string,
    public name: string,
    public value: any,
    public format: Format = null,
    public visible: FormControl<boolean> = null,
    public enabled = false,
    public isMiscField = false,
    public miscFieldNumber?: number,
  ) {}
}
